/**
 * Symbol registry — the single source for all chemistry symbol definitions.
 *
 * Consumers call getSymbol(id) to look up metadata + render component.
 * All apps share this registry for consistent symbol rendering.
 */

import type { SymbolDefinition } from './types';
import { ArrowSingle } from './symbols/ArrowSingle';
import { ArrowEquilibrium } from './symbols/ArrowEquilibrium';
import { ArrowRetrosynthetic } from './symbols/ArrowRetrosynthetic';
import { ArrowResonance } from './symbols/ArrowResonance';
import { Plus } from './symbols/Plus';

const SYMBOLS: Map<string, SymbolDefinition> = new Map();

function register(def: SymbolDefinition) {
  SYMBOLS.set(def.id, def);
}

// ── v1 Symbols ──────────────────────────────────────────────────────────

register({
  kind: 'arrow',
  id: 'arrow-single',
  displayName: 'Reaction Arrow',
  ariaLabel: 'Reaction arrow pointing right',
  category: 'reaction-arrow',
  defaultShaftLength: 20,
  render: ArrowSingle,
});

register({
  kind: 'arrow',
  id: 'arrow-equilibrium',
  displayName: 'Equilibrium',
  ariaLabel: 'Equilibrium arrows',
  category: 'reaction-arrow',
  defaultShaftLength: 20,
  render: ArrowEquilibrium,
});

register({
  kind: 'arrow',
  id: 'arrow-retrosynthetic',
  displayName: 'Retrosynthetic Arrow',
  ariaLabel: 'Retrosynthetic arrow',
  category: 'reaction-arrow',
  defaultShaftLength: 20,
  render: ArrowRetrosynthetic,
});

register({
  kind: 'arrow',
  id: 'arrow-resonance',
  displayName: 'Resonance Arrow',
  ariaLabel: 'Resonance double-headed arrow',
  category: 'reaction-arrow',
  defaultShaftLength: 20,
  render: ArrowResonance,
});

register({
  kind: 'simple',
  id: 'plus',
  displayName: 'Plus Sign',
  ariaLabel: 'Plus sign',
  category: 'notation',
  render: Plus,
});

// ── Public API ──────────────────────────────────────────────────────────

export function getSymbol(id: string): SymbolDefinition | undefined {
  return SYMBOLS.get(id);
}

export function getAllSymbols(): SymbolDefinition[] {
  return Array.from(SYMBOLS.values());
}

export function getSymbolsByCategory(category: string): SymbolDefinition[] {
  return Array.from(SYMBOLS.values()).filter(s => s.category === category);
}
